import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '@/app/model/store';

const selectTemplatesState = (state: RootState) => state.templates;

// === Document Template selectors ===

export const selectDocumentTemplates = createSelector(
  selectTemplatesState,
  (templatesState) =>
    templatesState.documentTemplateSummaries.filter(
      (template) => template.status !== 'deleted',
    ),
);

export const selectActiveDocumentTemplates = createSelector(
  selectTemplatesState,
  (templatesState) =>
    templatesState.documentTemplateSummaries.filter(
      (template) => template.status === 'active',
    ),
);

export const selectAllDocumentTemplates = createSelector(
  selectTemplatesState,
  (templatesState) => templatesState.documentTemplateSummaries,
);

export const selectDocumentTemplateById = (id: string) => (state: RootState) =>
  state.templates.documentTemplateById[id];

export const selectDocumentTemplateByIdLoading =
  (id: string) => (state: RootState) =>
    state.templates.documentTemplateByIdLoading[id] === true;

export const selectDocumentTemplatesByOrg =
  (orgId: string) => (state: RootState) =>
    state.templates.documentTemplateSummaries.filter(
      (template) =>
        template.organization_id === orgId && template.status !== 'deleted',
    );

export const selectDocumentTemplatesLoading = createSelector(
  selectTemplatesState,
  (templatesState) => templatesState.documentTemplatesLoading,
);

// === Encounter Schema selectors ===

export const selectEncounterSchemas = createSelector(
  selectTemplatesState,
  (templatesState) =>
    templatesState.encounterSchemas.filter(
      (schema) => schema.status !== 'deleted',
    ),
);

// Only published schemas — used by end-user flows (e.g. transcriptor specialty
// picker) that must not surface draft or archived versions.
export const selectPublishedEncounterSchemas = createSelector(
  selectTemplatesState,
  (templatesState) =>
    templatesState.encounterSchemas.filter(
      (schema) => schema.status === 'active',
    ),
);

export const selectAllEncounterSchemas = createSelector(
  selectTemplatesState,
  (templatesState) => templatesState.encounterSchemas,
);

export const selectEncounterSchemaById = (id: string) => (state: RootState) =>
  state.templates.encounterSchemas.find((schema) => schema.id === id);

export const selectEncounterSchemasLoading = createSelector(
  selectTemplatesState,
  (templatesState) => templatesState.encounterSchemasLoading,
);

export const selectEncounterSchemasListError = createSelector(
  selectTemplatesState,
  (templatesState) => templatesState.encounterSchemasListError,
);

export const selectEncounterSchemasByAnalyticsModelSeriesId = createSelector(
  [selectEncounterSchemas],
  (schemas) =>
    schemas.reduce<Map<string, typeof schemas>>((map, schema) => {
      const seriesId = schema.analytics_model_series_id;
      if (!seriesId) {
        return map;
      }

      const list = map.get(seriesId) ?? [];
      map.set(seriesId, [...list, schema]);
      return map;
    }, new Map()),
);

// === Error selector ===

export const selectTemplatesError = createSelector(
  selectTemplatesState,
  (templatesState) => templatesState.error,
);
